import { useCallback, useEffect, useState } from 'react';

import { Box, Group, Radio, SimpleGrid } from '@mantine/core';
import { UseFormReturnType } from '@mantine/form';
import { useTranslation } from 'react-i18next';

import {
  DECIMAL_PROPS,
  InputLabel,
  NumberInput,
  Select,
  Text,
  TextInput,
  YesNoRadioButton,
} from 'components';
import { CreditOptionsResponse, CreditRequestForm, CreditResponse, HousingExpense } from 'types';
import SectionTitle, { SectionTitleIndicatorType } from 'pages/clients/view/components/section-title';
import {
  comparisonValuesDiffer,
  hasComparisonValue,
  normalizeComparisonValue,
} from 'pages/clients/view/credit/request/preliminary-comparison';
import PreliminaryComparisonModal, {
  PreliminaryComparisonRow,
} from 'pages/clients/view/credit/request/preliminary-comparison-modal';

import { CreditComparisonSource } from '../comparison-source';
import { useCreditRequestMode } from '../request-mode-context';
import CreditRequestExpenses from './expenses';

interface CreditRequestOutcomesProps {
  credit: CreditResponse;
  form: UseFormReturnType<CreditRequestForm>;
  visibleFields: string[];
  options: CreditOptionsResponse;
  readOnly?: boolean;
  comparisonSource?: CreditComparisonSource | null;
}

type ComparisonFieldConfig = {
  key: string;
  label: string;
  getCurrentValue: () => any;
  getSourceValue: () => any;
  formatValue?: (value: any) => string;
  normalizeForComparison?: (value: any) => any;
  applyValue?: (value: any) => void;
};

const CreditRequestOutcomes = ({
  credit,
  form,
  visibleFields,
  options,
  readOnly = false,
  comparisonSource,
}: CreditRequestOutcomesProps) => {
  const { t } = useTranslation();
  const { isPreliminary } = useCreditRequestMode();
  const currentValues = form.getValues();
  const comparisonCredit = comparisonSource?.credit;
  const comparisonIndicatorType = comparisonSource?.type === 'previous' ? 'history' : 'warning';
  const canApplyComparisonSource = !!comparisonSource;
  const [comparisonOpened, setComparisonOpened] = useState(false);

  const getInitialHousingExpense = (): HousingExpense | undefined => {
    if (credit.mortgage) return 'MORTGAGE';
    if (credit.rent) return 'RENT';
    return undefined;
  };

  const [housingExpense, setHousingExpense] = useState<HousingExpense | undefined>(getInitialHousingExpense);

  const deriveHousingExpense = (values: Pick<CreditRequestForm, 'mortgage' | 'rent'>) => {
    if (values.mortgage) return 'MORTGAGE';
    if (values.rent) return 'RENT';
    return null;
  };

  const normalizeExpensesComparisonValue = (value: CreditResponse['expenses']) =>
    (value ?? []).map((item) => ({
      expenseType: item.expenseType ?? null,
      amount: item.amount ?? null,
    }));

  const formatYesNoValue = (value: boolean | null | undefined) =>
    value === true ? t('yes') : value === false ? t('no') : '-';

  const formatExpensesValue = (value: CreditResponse['expenses']) => {
    if (!value || value.length === 0) {
      return '-';
    }

    return value
      .map((item, index) => {
        const parts = [
          item.expenseType ? t(`credit.expenseType.options.${item.expenseType}`) : undefined,
          hasComparisonValue(item.amount) ? String(item.amount) : undefined,
        ].filter(Boolean);

        return `${index + 1}. ${parts.join(', ')}`;
      })
      .join('; ');
  };

  const currentHasExpenses =
    currentValues.expenses?.some(
      (item) => hasComparisonValue(item.expenseType) || hasComparisonValue(item.amount),
    ) ?? false;

  const comparisonHasExpenses =
    comparisonCredit?.expenses?.some(
      (item) => hasComparisonValue(item.expenseType) || hasComparisonValue(item.amount),
    ) ?? false;

  const comparisonHasOutcomesData =
    hasComparisonValue(comparisonCredit?.houseSituation) ||
    hasComparisonValue(
      comparisonCredit
        ? deriveHousingExpense({
            mortgage: comparisonCredit.mortgage,
            rent: comparisonCredit.rent,
          })
        : null,
    ) ||
    hasComparisonValue(comparisonCredit?.mortgageAmount) ||
    hasComparisonValue(comparisonCredit?.rentAmount) ||
    hasComparisonValue(comparisonCredit?.healthInsuranceAmount) ||
    hasComparisonValue(comparisonCredit?.healthInsuranceDiscount) ||
    hasComparisonValue(comparisonCredit?.enforcedCollection) ||
    comparisonHasExpenses;

  const comparisonFields: ComparisonFieldConfig[] = [
    {
      key: 'houseSituation',
      label: t('credit.houseSituation.label'),
      getCurrentValue: () => currentValues.houseSituation,
      getSourceValue: () => comparisonCredit?.houseSituation,
      formatValue: (value) => (value ? t(`credit.houseSituation.options.${value}`) : '-'),
      applyValue: (value) => form.setFieldValue('houseSituation', value ?? undefined),
    },
    {
      key: 'housingExpense',
      label: t('credit.mortgageOrRent.label'),
      getCurrentValue: () => deriveHousingExpense(currentValues),
      getSourceValue: () =>
        comparisonCredit
          ? deriveHousingExpense({
              mortgage: comparisonCredit.mortgage,
              rent: comparisonCredit.rent,
            })
          : null,
      formatValue: (value) => (value ? t(`credit.mortgageOrRent.options.${value}`) : '-'),
      applyValue: (value) => {
        setHousingExpense(value ?? undefined);
        if (value === 'MORTGAGE') {
          form.setFieldValue('mortgage', true);
          form.setFieldValue('rent', undefined);
          return;
        }
        if (value === 'RENT') {
          form.setFieldValue('rent', true);
          form.setFieldValue('mortgage', undefined);
          return;
        }
        form.setFieldValue('mortgage', undefined);
        form.setFieldValue('rent', undefined);
      },
    },
    {
      key: 'mortgageAmount',
      label: t('credit.mortgageAmount.label'),
      getCurrentValue: () => currentValues.mortgageAmount,
      getSourceValue: () => comparisonCredit?.mortgageAmount,
      applyValue: (value) => form.setFieldValue('mortgageAmount', value ?? undefined),
    },
    {
      key: 'rentAmount',
      label: t('credit.rentAmount.label'),
      getCurrentValue: () => currentValues.rentAmount,
      getSourceValue: () => comparisonCredit?.rentAmount,
      applyValue: (value) => form.setFieldValue('rentAmount', value ?? undefined),
    },
    {
      key: 'healthInsuranceAmount',
      label: t('credit.healthInsuranceAmount.label'),
      getCurrentValue: () => currentValues.healthInsuranceAmount,
      getSourceValue: () => comparisonCredit?.healthInsuranceAmount,
      applyValue: (value) => form.setFieldValue('healthInsuranceAmount', value ?? undefined),
    },
    {
      key: 'healthInsuranceDiscount',
      label: t('credit.healthInsuranceDiscount.label'),
      getCurrentValue: () => currentValues.healthInsuranceDiscount,
      getSourceValue: () => comparisonCredit?.healthInsuranceDiscount,
      formatValue: formatYesNoValue,
      applyValue: (value) => form.setFieldValue('healthInsuranceDiscount', value ?? undefined),
    },
    {
      key: 'enforcedCollection',
      label: t('credit.enforcedCollection.label'),
      getCurrentValue: () => currentValues.enforcedCollection,
      getSourceValue: () => comparisonCredit?.enforcedCollection,
      formatValue: formatYesNoValue,
      applyValue: (value) => form.setFieldValue('enforcedCollection', value ?? undefined),
    },
    {
      key: 'additionalExpenses',
      label: t('credit.expenseType.anotherExpense.label'),
      getCurrentValue: () => currentValues.additionalExpenses ?? currentHasExpenses,
      getSourceValue: () => comparisonHasExpenses,
      formatValue: formatYesNoValue,
      applyValue: (value) => {
        form.setFieldValue('additionalExpenses', value ? true : false);
        if (!value) {
          form.setFieldValue('expenses', []);
        }
      },
    },
    {
      key: 'expenses',
      label: t('credit.expenses.title'),
      getCurrentValue: () => currentValues.expenses,
      getSourceValue: () => comparisonCredit?.expenses,
      formatValue: formatExpensesValue,
      normalizeForComparison: normalizeExpensesComparisonValue,
      applyValue: (value) => {
        const nextExpenses = (value ?? []).map((item: any) => ({
          creditRequestId: credit.id,
          expenseType: item.expenseType,
          amount: item.amount,
        }));
        form.setFieldValue('expenses', nextExpenses as any);
        form.setFieldValue('additionalExpenses', nextExpenses.length > 0);
      },
    },
  ];

  const fieldValuesDiffer = (field: ComparisonFieldConfig) => {
    const currentValue = field.getCurrentValue();
    const sourceValue = field.getSourceValue();

    return comparisonValuesDiffer(
      field.normalizeForComparison ? field.normalizeForComparison(sourceValue) : sourceValue,
      field.normalizeForComparison ? field.normalizeForComparison(currentValue) : currentValue,
    );
  };

  const fieldHasComparisonValue = (field: ComparisonFieldConfig) =>
    field.key === 'additionalExpenses' || field.key === 'expenses'
      ? comparisonHasExpenses
      : hasComparisonValue(field.getSourceValue());

  const isComparableField = (field: ComparisonFieldConfig) =>
    fieldHasComparisonValue(field) && fieldValuesDiffer(field);

  const outcomesWarning =
    !!comparisonCredit &&
    comparisonHasOutcomesData &&
    comparisonFields.some(isComparableField);

  const comparisonRows: PreliminaryComparisonRow[] =
    comparisonOpened && comparisonCredit
      ? comparisonFields
          .filter(isComparableField)
          .map((field) => {
            const formatValue =
              field.formatValue ??
              ((value: any) => String(normalizeComparisonValue(value) ?? '-'));

            return {
              key: field.key,
              label: field.label,
              source: <Text>{formatValue(field.getSourceValue())}</Text>,
              current: <Text>{formatValue(field.getCurrentValue())}</Text>,
            };
          })
      : [];

  const changeHousingExpense = useCallback(
    (val: HousingExpense) => {
      setHousingExpense(val);
      if (val === "MORTGAGE") {
        form.setFieldValue("mortgage", true);
        form.setFieldValue("rent", undefined);
      } else if (val === "RENT") {
        form.setFieldValue("rent", true);
        form.setFieldValue("mortgage", undefined);
      }
    },
    [form],
  );

  useEffect(() => {
    const formValues = form.getValues();
    if (Object.keys(formValues).length === 0) {
      return;
    }
    if (formValues.additionalExpenses === false) {
      //not using visibleFields here because expenses is an array
      form.setFieldValue('expenses', []);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.getValues().additionalExpenses]);

  const applyComparisonValues = () => {
    comparisonFields
      .filter(isComparableField)
      .forEach((field) => {
        const sourceValue = field.getSourceValue();

        if (field.applyValue) {
          field.applyValue(sourceValue);
          return;
        }
      });

    setComparisonOpened(false);
  };

  return (
    <Box p={'md'}>
      <SectionTitle
        label={t('credit.outcomes.sectionTitle')}
        hasWarning={outcomesWarning}
        onWarningClick={() => setComparisonOpened(true)}
        indicatorType={comparisonIndicatorType as SectionTitleIndicatorType}
      />
      <Box pt={'xs'} mt={'sm'}>
        <Box>
          <SimpleGrid cols={4}>
            <Box>
              <InputLabel label={t('credit.mortgageOrRent.label')} />
              <Group>
                <Radio
                  checked={housingExpense === 'RENT'}
                  onChange={() => changeHousingExpense('RENT')}
                  label={t('credit.mortgageOrRent.options.RENT')}
                  disabled={readOnly}
                />
                <Radio
                  checked={housingExpense === 'MORTGAGE'}
                  onChange={() => changeHousingExpense('MORTGAGE')}
                  label={t('credit.mortgageOrRent.options.MORTGAGE')}
                  disabled={readOnly}
                />
              </Group>
            </Box>
            <Select
              label={t('credit.houseSituation.label')}
              data={options['houseSituation'].map((el) => ({
                value: el,
                label: t(`credit.houseSituation.options.${el}`),
              }))}
              key={form.key('houseSituation')}
              {...form.getInputProps('houseSituation')}
              clearable={true}
              required={!isPreliminary}
              disabled={readOnly}
            />
            {visibleFields.includes("mortgageAmount") && (
              <NumberInput
                label={t('credit.mortgageAmount.label')}
                key={form.key('mortgageAmount')}
                {...form.getInputProps('mortgageAmount')}
                min={0}
                {...DECIMAL_PROPS}
                required={!isPreliminary}
                disabled={readOnly}
              />
            )}
            {visibleFields.includes("rentAmount") && (
              <NumberInput
                label={t('credit.rentAmount.label')}
                key={form.key('rentAmount')}
                {...form.getInputProps('rentAmount')}
                min={options['rentAmount']?.min}
                max={options['rentAmount']?.max}
                {...DECIMAL_PROPS}
                required={!isPreliminary}
                disabled={readOnly}
              />
            )}
          </SimpleGrid>
        </Box>

        <Box mt={'sm'}>
          <SimpleGrid cols={4}>
            <NumberInput
              label={t('credit.healthInsuranceAmount.label')}
              key={form.key('healthInsuranceAmount')}
              {...form.getInputProps('healthInsuranceAmount')}
              min={0}
              {...DECIMAL_PROPS}
              required={!isPreliminary}
              disabled={readOnly}
            />
            <YesNoRadioButton
              label={t('credit.healthInsuranceDiscount.label')}
              name={'healthInsuranceDiscount'}
              form={form}
              required={!isPreliminary}
              disabled={readOnly}
            />
            <YesNoRadioButton
              label={t('credit.enforcedCollection.label')}
              name={'enforcedCollection'}
              form={form}
              required={!isPreliminary}
              disabled={readOnly}
            />
            <YesNoRadioButton
              label={t(`credit.expenseType.anotherExpense.label`)}
              name={'additionalExpenses'}
              form={form}
              required={!isPreliminary}
              disabled={readOnly}
            />
          </SimpleGrid>
        </Box>

        {form.getValues().additionalExpenses && (
          <CreditRequestExpenses
            credit={credit}
            form={form}
            options={options}
            required={!isPreliminary}
            readOnly={readOnly}
          />
        )}

        <Box mt={'sm'}>
          <SimpleGrid cols={4}>
            <TextInput
              label={t('credit.comment.singular')}
              key={form.key('comment4')}
              {...form.getInputProps('comment4')}
              disabled={readOnly}
            />
          </SimpleGrid>
        </Box>
      </Box>

      <PreliminaryComparisonModal
        opened={comparisonOpened}
        onClose={() => setComparisonOpened(false)}
        onApply={applyComparisonValues}
        title={t(
          comparisonSource?.type === 'previous'
            ? 'credit.previousComparison.title'
            : 'credit.preliminaryComparison.title',
        )}
        emptyText={t('credit.preliminaryComparison.empty')}
        fieldLabel={t('credit.preliminaryComparison.field')}
        sourceLabel={t(
          comparisonSource?.type === 'previous'
            ? 'credit.previousComparison.previous'
            : 'credit.preliminaryComparison.preliminary',
        )}
        currentLabel={t('credit.preliminaryComparison.active')}
        applyLabel={t(
          comparisonSource?.type === 'previous'
            ? 'credit.previousComparison.apply'
            : 'credit.preliminaryComparison.apply',
        )}
        cancelLabel={t('credit.preliminaryComparison.cancel')}
        rows={comparisonRows}
        showApplyButton={canApplyComparisonSource}
      />
    </Box>
  );
};

export default CreditRequestOutcomes;
